import React, { useEffect, useMemo, useState } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Switch,
  Divider,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add,
  Edit,
  Delete,
  Search,
  Close,
  Schedule,
  SwapHoriz,
  EventNote,
  AccessTime,
  CheckCircle,
  Cancel,
  Pending,
  CalendarMonth,
  Visibility,
  Block,
} from "@mui/icons-material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import {
  useApproveScheduleChange,
  useRejectScheduleChange,
  useRequestScheduleChange,
  useScheduleChangeRequests,
  useTeacherTimetable,
  useUpdateScheduleChangeRequest,
  useCancelScheduleChangeRequest,
} from "../../hooks/queries/useTimetable";
import { toast } from "react-toastify";
import { format, parseISO, startOfMonth, endOfMonth, isWithinInterval } from "date-fns";
import { useSelector } from "react-redux";
import scheduleService from "../../services/scheduleService";

function wallClockStringToDate(value) {
  if (!value) return null;
  const hm = String(value).trim().slice(0, 5);
  const [h, m] = hm.split(":").map((x) => parseInt(x, 10));
  if (!Number.isFinite(h)) return null;
  const d = new Date();
  d.setHours(h, Number.isFinite(m) ? m : 0, 0, 0);
  return d;
}

/** API may omit top-level `requestedBy` but include nested `requester.id` (Sequelize JSON). */
function getScheduleChangeRequesterUserId(raw) {
  if (!raw) return null;
  const v = raw.requestedBy ?? raw.requester?.id;
  if (v === undefined || v === null || v === "") return null;
  return v;
}

function userIdsMatch(a, b) {
  if (a === undefined || a === null || b === undefined || b === null) return false;
  return String(a) === String(b);
}

const TimetableScheduleChange = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [slotView, setSlotView] = useState("weekly");
  const [slotReferenceDate, setSlotReferenceDate] = useState(new Date());
  const [datedSchedules, setDatedSchedules] = useState([]);
  const [datedSchedulesLoading, setDatedSchedulesLoading] = useState(false);
  const [formData, setFormData] = useState({
    slotRowKey: "",
    timeTableSlotId: "",
    preferredDate: null,
    newStartTime: null,
    newEndTime: null,
    reason: "",
  });
  const [editingRequestId, setEditingRequestId] = useState(null);
  const [viewRequestRow, setViewRequestRow] = useState(null);
  const [cancelTargetId, setCancelTargetId] = useState(null);

  const { user } = useSelector((state) => state.auth || {});
  const currentUserId = user?.id;
  const teacherId = user?.teacherId;
  const userRoleSlug = String(user?.role?.slug || user?.role || "")
    .trim()
    .toLowerCase();
  // Normalize away spaces/underscores/dashes so "super admin"/"super_admin" all match.
  const normalizedRole = userRoleSlug.replace(/[\s_-]+/g, "");
  const isSuperAdminRole =
    normalizedRole === "superadmin" || normalizedRole === "superadministrator";
  // Super admin has all permissions, so they can review (approve/reject) requests too.
  const canReview =
    isSuperAdminRole ||
    userRoleSlug === "admin" ||
    userRoleSlug === "institute" ||
    userRoleSlug === "hod";
  /** Submitting new requests is for teaching staff (teacher/HOD); institute, campus admin and super admin only review here. */
  const showRequestChangeButton =
    !isSuperAdminRole && userRoleSlug !== "admin" && userRoleSlug !== "institute";

  const [searchParams, setSearchParams] = useSearchParams();
  const requestIdFromUrl = searchParams.get("requestId");

  // Fetch schedule change requests from API
  const { data: requestsData, isLoading, error } = useScheduleChangeRequests();
  const { data: teacherTimetableData } = useTeacherTimetable(teacherId);
  const requestChangeMutation = useRequestScheduleChange();
  const approveMutation = useApproveScheduleChange();
  const rejectMutation = useRejectScheduleChange();
  const updateRequestMutation = useUpdateScheduleChangeRequest();
  const cancelRequestMutation = useCancelScheduleChangeRequest();
  const scheduleRequests = requestsData?.data?.data.requests || [];
  const teacherSlots = teacherTimetableData?.data?.data?.slots || [];

  const slotOptions = useMemo(() => {
    return teacherSlots
      .filter((slot) => !!slot?.id)
      .map((slot) => {
        const className = slot?.class?.name || "N/A";
        const subjectName = slot?.subject?.name || "N/A";
        const day = slot?.dayOfWeek || "N/A";
        const start = String(slot?.startTime || "").slice(0, 5);
        const end = String(slot?.endTime || "").slice(0, 5);
        return {
          id: Number(slot.id),
          rowKey: `w-${Number(slot.id)}`,
          label: `${className} - ${subjectName} (${day} ${start}-${end})`,
          raw: slot,
        };
      });
  }, [teacherSlots]);

  useEffect(() => {
    if (!requestDialogOpen || slotView !== "monthly") return;
    let active = true;
    const loadSchedules = async () => {
      try {
        setDatedSchedulesLoading(true);
        const dateParam = slotReferenceDate ? format(new Date(slotReferenceDate), "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd");
        const res = await scheduleService.getTimetable({ date: dateParam, view: "monthly" });
        const data = Array.isArray(res?.data?.data) ? res.data.data : [];
        if (active) setDatedSchedules(data);
      } catch (_e) {
        if (active) setDatedSchedules([]);
      } finally {
        if (active) setDatedSchedulesLoading(false);
      }
    };
    loadSchedules();
    return () => {
      active = false;
    };
  }, [requestDialogOpen, slotView, slotReferenceDate]);

  const monthlySlotOptions = useMemo(() => {
    if (!Array.isArray(datedSchedules) || datedSchedules.length === 0) return [];
    const ref = slotReferenceDate ? new Date(slotReferenceDate) : new Date();
    const monthStart = startOfMonth(ref);
    const monthEnd = endOfMonth(ref);

    const findSlotId = (scheduleItem) => {
      const ymd = scheduleItem?.date ? String(scheduleItem.date).slice(0, 10) : "";
      const scheduleDay = ymd
        ? format(parseISO(ymd), "EEEE").toUpperCase()
        : "";
      const scheduleStart = String(scheduleItem?.startTime || "").slice(0, 5);
      const scheduleEnd = String(scheduleItem?.endTime || "").slice(0, 5);
      const scheduleClassId = Number(scheduleItem?.classId || 0);
      const scheduleSubjectId = Number(scheduleItem?.subjectId || 0);
      const scheduleTeacherId = Number(scheduleItem?.teacherId || 0);
      const scheduleBatch = String(scheduleItem?.practicalBatchCode || "").toUpperCase();
      const matched = teacherSlots.find((slot) => {
        const slotDay = String(slot?.dayOfWeek || "").toUpperCase();
        const slotStart = String(slot?.startTime || "").slice(0, 5);
        const slotEnd = String(slot?.endTime || "").slice(0, 5);
        const slotClassId = Number(slot?.class?.id || slot?.classId || 0);
        const slotSubjectId = Number(slot?.subject?.id || slot?.subjectId || 0);
        const slotTeacherId = Number(slot?.teacher?.id || slot?.teacherId || teacherId || 0);
        const slotBatch = String(slot?.practicalBatchCode || "").toUpperCase();
        return (
          slotDay === scheduleDay &&
          slotStart === scheduleStart &&
          slotEnd === scheduleEnd &&
          slotClassId === scheduleClassId &&
          slotSubjectId === scheduleSubjectId &&
          slotTeacherId === scheduleTeacherId &&
          slotBatch === scheduleBatch
        );
      });
      return Number(matched?.id || 0) || null;
    };

    return datedSchedules
      .filter((entry) => {
        const d = entry?.date ? String(entry.date).slice(0, 10) : "";
        if (!d) return false;
        let di;
        try {
          di = parseISO(d);
        } catch {
          return false;
        }
        return isWithinInterval(di, { start: monthStart, end: monthEnd });
      })
      .map((entry) => {
        const fromApi = Number(entry.timeTableSlotId ?? entry.slotId ?? 0);
        const slotId = Number.isInteger(fromApi) && fromApi > 0 ? fromApi : findSlotId(entry);
        if (!slotId) return null;
        const ymd = entry?.date ? String(entry.date).slice(0, 10) : "";
        const dateStr = ymd ? format(parseISO(ymd), "dd-MM-yyyy") : "N/A";
        const day = entry?.dayOfWeek || (ymd ? format(parseISO(ymd), "EEEE") : entry?.dayName || "N/A");
        const start = String(entry?.startTime || "").slice(0, 5);
        const end = String(entry?.endTime || "").slice(0, 5);
        const rowKey = `m-${slotId}-${ymd}`;
        return {
          id: slotId,
          rowKey,
          date: entry?.date || null,
          label: `${entry?.classDisplayName || entry?.className || "N/A"} - ${entry?.subjectName || "N/A"} (${day}, ${dateStr} ${start}-${end})`,
          scheduleEntry: entry,
        };
      })
      .filter(Boolean);
  }, [datedSchedules, teacherSlots, teacherId, slotReferenceDate]);

  const displaySlotOptions = slotView === "monthly" ? monthlySlotOptions : slotOptions;

  const selectedSlot = useMemo(() => {
    const selectedId = Number(formData.timeTableSlotId || 0);
    if (!selectedId) return null;
    if (slotView === "monthly") {
      const mo = formData.slotRowKey
        ? monthlySlotOptions.find((s) => s.rowKey === formData.slotRowKey)
        : monthlySlotOptions.find((s) => Number(s.id) === selectedId);
      if (mo?.scheduleEntry) {
        const e = mo.scheduleEntry;
        return {
          class: { name: e.classDisplayName || e.className },
          subject: { name: e.subjectName },
          dayOfWeek: e.dayOfWeek || "",
          startTime: e.startTime,
          endTime: e.endTime,
        };
      }
    }
    return slotOptions.find((opt) => opt.id === selectedId)?.raw || null;
  }, [slotOptions, monthlySlotOptions, slotView, formData.timeTableSlotId, formData.slotRowKey]);

  // Transform API data for display
  const transformedRequests = scheduleRequests.map((request) => ({
    id: request.id,
    teacher:
      request.timeTableSlot?.teacher?.user?.fullName ||
      `${request.timeTableSlot?.teacher?.user?.firstName || ""} ${request.timeTableSlot?.teacher?.user?.lastName || ""}`.trim() ||
      request.timeTableSlot?.teacher?.User?.fullName ||
      `${request.timeTableSlot?.teacher?.User?.firstName || ""} ${request.timeTableSlot?.teacher?.User?.lastName || ""}`.trim() ||
      "N/A",
    class: request.timeTableSlot?.class?.name || 'N/A',
    subject: request.timeTableSlot?.subject?.name || 'N/A',
    originalDate: request.originalDay || 'N/A',
    originalTime: request.originalStartTime && request.originalEndTime ? 
      `${request.originalStartTime} - ${request.originalEndTime}` : 
      'N/A',
    newDate: request.newDay || 'N/A',
    newTime: request.newStartTime && request.newEndTime ? 
      `${request.newStartTime} - ${request.newEndTime}` : 
      'N/A',
    reason: request.reason,
    changeType: request.changeType,
    swapWith: request.swapWithTeacher?.User ? 
      `${request.swapWithTeacher.User.firstName} ${request.swapWithTeacher.User.lastName}` : 
      null,
    status: request.status,
    priority: request.priority,
    requestedAt: request.createdAt ? format(new Date(request.createdAt), 'yyyy-MM-dd HH:mm') : 'N/A',
    requestedBy: request.requester ? 
      `${request.requester.firstName || ''} ${request.requester.lastName || ''}`.trim() || request.requester.email : 
      'N/A',
    approvedBy: request.reviewer ? 
      `${request.reviewer.firstName} ${request.reviewer.lastName}` : 
      null,
    approvedAt: request.reviewedAt ? format(new Date(request.reviewedAt), 'yyyy-MM-dd HH:mm') : null,
    rejectedBy: request.reviewer ? 
      `${request.reviewer.firstName} ${request.reviewer.lastName}` : 
      null,
    rejectedAt: request.reviewedAt ? format(new Date(request.reviewedAt), 'yyyy-MM-dd HH:mm') : null,
    rejectionReason: request.rejectionReason,
    rawData: request // Keep original data for reference
  }));

  // Handle navigation from notification (just switch to pending tab if applicable)
  useEffect(() => {
    if (requestIdFromUrl && transformedRequests.length > 0) {
      const request = transformedRequests.find((r) => String(r.id) === String(requestIdFromUrl));
      if (request && request.status === "pending" && canReview) {
        setActiveTab(1);
      }
      // Clear the param after handling
      setSearchParams({}, { replace: true });
    }
  }, [requestIdFromUrl, transformedRequests, setSearchParams, canReview]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle fontSize="small" />;
      case "rejected":
        return <Cancel fontSize="small" />;
      case "pending":
        return <Pending fontSize="small" />;
      case "cancelled":
        return <Close fontSize="small" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "pending":
        return "warning";
      case "cancelled":
        return "default";
      default:
        return "default";
    }
  };

  const columns = [
    {
      field: "teacher",
      headerName: "Teacher",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32 }}>{params.value[0]}</Avatar>
          {params.value}
        </Box>
      ),
    },
    {
      field: "class",
      headerName: "Class",
      width: 80,
    },
    {
      field: "subject",
      headerName: "Subject",
      width: 120,
    },
    {
      field: "originalDate",
      headerName: "Original Schedule",
      width: 180,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">{params.row.originalDate}</Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.originalTime}
          </Typography>
        </Box>
      ),
    },
    {
      field: "newDate",
      headerName: "Requested Schedule",
      width: 180,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">{params.row.newDate}</Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.newTime}
          </Typography>
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          icon={getStatusIcon(params.value)}
          label={
            params.value
              ? String(params.value).charAt(0).toUpperCase() + String(params.value).slice(1)
              : ""
          }
          size="small"
          color={getStatusColor(params.value)}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 280,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        const requesterId = getScheduleChangeRequesterUserId(row.rawData);
        const isOwner = userIdsMatch(requesterId, currentUserId);
        const pending = row.status === "pending";
        // Teacher API list is scoped to own requests; still require match when we have a requester id (HOD/admin may use same screen).
        const canEditOrWithdrawPending =
          pending &&
          (isOwner || (userRoleSlug === "teacher" && !canReview));

        return (
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "nowrap" }}>
            <Tooltip title="View details">
              <IconButton size="small" onClick={() => setViewRequestRow(row)}>
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
            {pending && canReview && (
              <>
                <Tooltip title="Approve">
                  <IconButton
                    size="small"
                    color="success"
                    disabled={approveMutation.isPending || approveMutation.isLoading}
                    onClick={() => {
                      setSelectedRequest(row);
                      approveMutation
                        .mutateAsync(row.id)
                        .then(() => {
                          toast.success("Request approved");
                          setSelectedRequest(null);
                        })
                        .catch((e) => {
                          toast.error(e?.response?.data?.message || "Failed to approve request");
                        });
                    }}
                  >
                    <CheckCircle fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reject">
                  <IconButton
                    size="small"
                    color="error"
                    disabled={rejectMutation.isPending || rejectMutation.isLoading}
                    onClick={() => {
                      setSelectedRequest(row);
                      setRejectReason("");
                      setRejectDialogOpen(true);
                    }}
                  >
                    <Cancel fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {canEditOrWithdrawPending && (
              <>
                <Tooltip title="Edit (until approved or rejected)">
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={updateRequestMutation.isPending}
                    onClick={() => {
                      const r = row.rawData;
                      const slotId = r?.timeTableSlotId;
                      setEditingRequestId(r.id);
                      setSlotView("weekly");
                      setFormData({
                        slotRowKey: slotId ? `w-${slotId}` : "",
                        timeTableSlotId: slotId ? String(slotId) : "",
                        preferredDate: r.preferredDate
                          ? parseISO(String(r.preferredDate).slice(0, 10))
                          : null,
                        newStartTime: wallClockStringToDate(r.newStartTime),
                        newEndTime: wallClockStringToDate(r.newEndTime),
                        reason: r.reason || "",
                      });
                      setRequestDialogOpen(true);
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Withdraw request">
                  <IconButton
                    size="small"
                    color="warning"
                    disabled={cancelRequestMutation.isPending}
                    onClick={() => setCancelTargetId(row.id)}
                  >
                    <Block fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        );
      },
    },
  ];

  const closeRequestDialog = () => {
    setRequestDialogOpen(false);
    setEditingRequestId(null);
    setFormData({
      slotRowKey: "",
      timeTableSlotId: "",
      preferredDate: null,
      newStartTime: null,
      newEndTime: null,
      reason: "",
    });
  };

  const handleSubmitRequest = async () => {
    try {
      const { timeTableSlotId, reason, preferredDate, newStartTime, newEndTime } = formData;
      if (!reason?.trim()) {
        toast.error("Reason is required");
        return;
      }

      const hasStart = !!newStartTime;
      const hasEnd = !!newEndTime;
      if (hasStart !== hasEnd) {
        toast.error("Please provide both new start and end time, or leave both empty");
        return;
      }

      let formattedStart = null;
      let formattedEnd = null;
      if (hasStart && hasEnd) {
        formattedStart = format(new Date(newStartTime), "HH:mm");
        formattedEnd = format(new Date(newEndTime), "HH:mm");
        if (formattedEnd <= formattedStart) {
          toast.error("New end time must be after new start time");
          return;
        }
      }

      const slotId = Number(timeTableSlotId);
      if (!editingRequestId && (!Number.isInteger(slotId) || slotId <= 0)) {
        toast.error("Please select a timetable slot");
        return;
      }

      const selectedMonthlySlot =
        slotView === "monthly"
          ? (formData.slotRowKey
              ? monthlySlotOptions.find((s) => s.rowKey === formData.slotRowKey)
              : monthlySlotOptions.find((s) => Number(s.id) === slotId))
          : null;
      const preferredDateValue = preferredDate
        ? format(new Date(preferredDate), "yyyy-MM-dd")
        : selectedMonthlySlot?.date
          ? String(selectedMonthlySlot.date).slice(0, 10)
          : null;

      const dayFromPreferredDate = preferredDate
        ? format(new Date(preferredDate), "EEEE").toUpperCase()
        : selectedMonthlySlot?.date
          ? format(new Date(String(selectedMonthlySlot.date).slice(0, 10)), "EEEE").toUpperCase()
        : String(selectedSlot?.dayOfWeek || "").toUpperCase() || null;

      if (editingRequestId) {
        const body = {
          reason: reason.trim(),
          preferredDate: preferredDateValue,
          preferredTime: formattedStart,
          newDay: dayFromPreferredDate,
          newStartTime: hasStart ? formattedStart : null,
          newEndTime: hasEnd ? formattedEnd : null,
        };
        await updateRequestMutation.mutateAsync({ id: editingRequestId, body });
        toast.success("Request updated");
        closeRequestDialog();
        return;
      }

      const requestBody = {
        timeTableSlotId: slotId,
        changeType: "time_change",
        reason: reason.trim(),
        newDay: dayFromPreferredDate,
        newStartTime: formattedStart,
        newEndTime: formattedEnd,
        preferredDate: preferredDateValue,
        preferredTime: formattedStart,
      };
      await requestChangeMutation.mutateAsync(requestBody);
      toast.success("Schedule change request submitted");
      closeRequestDialog();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to submit request";
      toast.error(msg);
    }
  };

  const handleRejectRequest = async () => {
    if (!selectedRequest?.id) return;
    try {
      await rejectMutation.mutateAsync({ requestId: selectedRequest.id, reason: rejectReason || "Rejected" });
      toast.success("Request rejected");
      setRejectDialogOpen(false);
      setSelectedRequest(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to reject request");
    }
  };

  const pendingRequests = transformedRequests.filter((r) => r.status === "pending");
  const approvedRequests = transformedRequests.filter(
    (r) => r.status === "approved"
  );
  const myRequests = useMemo(() => {
    if (userRoleSlug === "teacher" && !canReview) return transformedRequests;
    if (currentUserId == null) return transformedRequests;
    return transformedRequests.filter((r) =>
      userIdsMatch(getScheduleChangeRequesterUserId(r.rawData), currentUserId)
    );
  }, [transformedRequests, currentUserId, userRoleSlug, canReview]);

  const rejectedRequests = transformedRequests.filter(
    (r) => r.status === "rejected"
  );

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Timetable & Schedule Changes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Request and manage schedule change requests
          </Typography>
        </Box>
        {showRequestChangeButton && (
          <Button
            variant="contained"
            startIcon={<SwapHoriz />}
            onClick={() => {
              setEditingRequestId(null);
              setFormData({
                slotRowKey: "",
                timeTableSlotId: "",
                preferredDate: null,
                newStartTime: null,
                newEndTime: null,
                reason: "",
              });
              setSlotView("weekly");
              setRequestDialogOpen(true);
            }}
          >
            Request Change
          </Button>
        )}
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          {
            label: "Pending Requests",
            value: pendingRequests.length,
            color: "warning.main",
            icon: <Pending />,
          },
          {
            label: "Approved",
            value: approvedRequests.length,
            color: "success.main",
            icon: <CheckCircle />,
          },
          {
            label: "Rejected",
            value: rejectedRequests.length,
            color: "error.main",
            icon: <Cancel />,
          },
          {
            label: "Total Requests",
            value: transformedRequests.length,
            color: "info.main",
            icon: <Schedule />,
          },
        ].map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card>
              <CardContent
                sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}
              >
                <Avatar sx={{ bgcolor: stat.color }}>{stat.icon}</Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="All Requests" icon={<Schedule />} iconPosition="start" />
          <Tab
            label={`Pending (${pendingRequests.length})`}
            icon={<Pending />}
            iconPosition="start"
          />
          <Tab label="My Requests" icon={<EventNote />} iconPosition="start" />
        </Tabs>
      </Card>

      {/* Content */}
      <Card>
        <CardContent>
          <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
            <TextField
              placeholder="Search requests..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" defaultValue="">
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Loading State */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load schedule change requests. {error.message}
            </Alert>
          )}

          {/* Data Grid */}
          {!isLoading && !error && (
            <DataGrid
              rows={
                activeTab === 0
                  ? transformedRequests
                  : activeTab === 1
                  ? pendingRequests
                  : myRequests
              }
              columns={columns}
              autoHeight
              disableRowSelectionOnClick
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              sx={{ border: "none" }}
            />
          )}
        </CardContent>
      </Card>

      {/* Request Schedule Change Dialog */}
      <Dialog
        open={requestDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          closeRequestDialog();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            {editingRequestId ? "Edit Schedule Change Request" : "Request Schedule Change"}
            <IconButton onClick={closeRequestDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Alert severity="info">
                {editingRequestId
                  ? "Update your pending request. The timetable slot cannot be changed here—withdraw and submit a new request if you need a different slot."
                  : "Request a change to your scheduled class. The request will be reviewed by the admin."}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <Divider>
                <Chip label="Original Schedule" />
              </Divider>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Timetable Slot</InputLabel>
                <Select
                  value={slotView === "monthly" ? formData.slotRowKey || "" : formData.timeTableSlotId || ""}
                  label="Timetable Slot"
                  disabled={!!editingRequestId}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (slotView === "monthly") {
                      const opt = monthlySlotOptions.find((s) => s.rowKey === v);
                      if (!opt) {
                        setFormData((prev) => ({ ...prev, slotRowKey: "", timeTableSlotId: "" }));
                        return;
                      }
                      setFormData((prev) => ({
                        ...prev,
                        slotRowKey: v,
                        timeTableSlotId: String(opt.id),
                        preferredDate: opt.date ? parseISO(String(opt.date).slice(0, 10)) : prev.preferredDate,
                      }));
                      return;
                    }
                    setFormData((prev) => ({
                      ...prev,
                      slotRowKey: v ? `w-${v}` : "",
                      timeTableSlotId: v,
                    }));
                  }}
                >
                  <MenuItem value="">Select Slot</MenuItem>
                  {displaySlotOptions.map((slot) => (
                    <MenuItem
                      key={slot.rowKey || slot.id}
                      value={slotView === "monthly" ? slot.rowKey || slot.id : String(slot.id)}
                    >
                      {slot.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Schedule View</InputLabel>
                <Select
                  value={slotView}
                  label="Schedule View"
                  disabled={!!editingRequestId}
                  onChange={(e) => {
                    setSlotView(e.target.value);
                    setFormData((prev) => ({ ...prev, timeTableSlotId: "", slotRowKey: "" }));
                  }}
                >
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <DatePicker
                label="Reference Date (month/week)"
                value={slotReferenceDate}
                onChange={(date) => setSlotReferenceDate(date || new Date())}
                disabled={!!editingRequestId}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            {slotView === "monthly" && !editingRequestId && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Monthly view lists every class in the calendar month of the reference date (with date on each row).
                </Alert>
              </Grid>
            )}
            {slotView === "monthly" && datedSchedulesLoading && !editingRequestId && (
              <Grid item xs={12}>
                <Alert severity="info">Loading monthly schedule slots...</Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Alert severity="info">
                {selectedSlot
                  ? `Selected: ${selectedSlot?.class?.name || "N/A"} - ${selectedSlot?.subject?.name || "N/A"} (${selectedSlot?.dayOfWeek || "N/A"} ${String(selectedSlot?.startTime || "").slice(0, 5)}-${String(selectedSlot?.endTime || "").slice(0, 5)})`
                  : "Please select a slot to request change."}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <Divider>
                <Chip label="New Schedule" color="primary" />
              </Divider>
            </Grid>
            <Grid item xs={6}>
              <DatePicker
                label="Preferred Date (Optional)"
                value={formData.preferredDate}
                onChange={(date) => setFormData({ ...formData, preferredDate: date })}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TimePicker
                label="New Start Time (Optional)"
                value={formData.newStartTime}
                onChange={(time) => setFormData({ ...formData, newStartTime: time })}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TimePicker
                label="New End Time (Optional)"
                value={formData.newEndTime}
                onChange={(time) => setFormData({ ...formData, newEndTime: time })}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason for Change"
                multiline
                rows={3}
                value={formData.reason}
                onChange={(e) =>
                  setFormData({ ...formData, reason: e.target.value })
                }
                placeholder="Please provide a reason for the schedule change..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeRequestDialog}
            disabled={
              requestChangeMutation.isPending ||
              requestChangeMutation.isLoading ||
              updateRequestMutation.isPending
            }
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitRequest}
            disabled={
              requestChangeMutation.isPending ||
              requestChangeMutation.isLoading ||
              updateRequestMutation.isPending
            }
          >
            {updateRequestMutation.isPending
              ? "Saving..."
              : requestChangeMutation.isPending || requestChangeMutation.isLoading
                ? "Submitting..."
                : editingRequestId
                  ? "Save Changes"
                  : "Submit Request"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!viewRequestRow}
        onClose={() => setViewRequestRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Schedule change details</DialogTitle>
        <DialogContent dividers>
          {viewRequestRow && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body2"><strong>Status:</strong> {viewRequestRow.status}</Typography>
              <Typography variant="body2"><strong>Class:</strong> {viewRequestRow.class}</Typography>
              <Typography variant="body2"><strong>Subject:</strong> {viewRequestRow.subject}</Typography>
              <Typography variant="body2"><strong>Original:</strong> {viewRequestRow.originalDate} {viewRequestRow.originalTime}</Typography>
              <Typography variant="body2"><strong>Requested:</strong> {viewRequestRow.newDate} {viewRequestRow.newTime}</Typography>
              <Typography variant="body2"><strong>Reason:</strong> {viewRequestRow.reason}</Typography>
              <Typography variant="body2"><strong>Submitted:</strong> {viewRequestRow.requestedAt}</Typography>
              {viewRequestRow.rejectionReason && (
                <Typography variant="body2" color="error"><strong>Rejection note:</strong> {viewRequestRow.rejectionReason}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewRequestRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!cancelTargetId} onClose={() => setCancelTargetId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Withdraw request?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This will cancel your pending schedule change. You can submit a new request later.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTargetId(null)}>No</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={cancelRequestMutation.isPending}
            onClick={async () => {
              if (!cancelTargetId) return;
              try {
                await cancelRequestMutation.mutateAsync(cancelTargetId);
                toast.success("Request withdrawn");
                setCancelTargetId(null);
              } catch (e) {
                toast.error(e?.response?.data?.message || "Failed to cancel request");
              }
            }}
          >
            {cancelRequestMutation.isPending ? "Withdrawing..." : "Yes, withdraw"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectDialogOpen} onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setRejectDialogOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Request</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleRejectRequest} disabled={rejectMutation.isPending}>
            {rejectMutation.isPending ? "Rejecting..." : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimetableScheduleChange;
